import Vue from "vue";
import Vuex from "vuex";
import JwtService from "@/core/services/jwt.service";
import ApiService from "@/core/services/api.service";

Vue.use(Vuex);

const SELECTED_STORE_KEY = "selectedStoreId";
const CURRENCY_STORAGE_KEY = "posCurrency";

function loadStoredCurrency() {
  try {
    const raw = window.localStorage.getItem(CURRENCY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

const storedCurrency = loadStoredCurrency();

function resolveSelectedStoreId(user) {
  if (!user?.stores?.length) {
    return null;
  }

  const saved = window.localStorage.getItem(SELECTED_STORE_KEY);
  if (saved && user.stores.some((s) => s.storeId === saved)) {
    return saved;
  }

  return user.stores[0].storeId;
}

const initialUser = JwtService.getUser();

export default new Vuex.Store({
  state: {
    user: initialUser,
    selectedStoreId: resolveSelectedStoreId(initialUser),
    currencyCode: storedCurrency?.currencyCode || "GHS",
    currencySymbol: storedCurrency?.currencySymbol || "GH₵ ",
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
      if (user) {
        JwtService.saveUser(user);
        state.selectedStoreId = resolveSelectedStoreId(user);
        if (state.selectedStoreId) {
          window.localStorage.setItem(SELECTED_STORE_KEY, state.selectedStoreId);
        }
      } else {
        JwtService.destroyUser();
        state.selectedStoreId = null;
        window.localStorage.removeItem(SELECTED_STORE_KEY);
      }
    },
    SET_STORE(state, storeId) {
      state.selectedStoreId = storeId;
      if (storeId) {
        window.localStorage.setItem(SELECTED_STORE_KEY, storeId);
      } else {
        window.localStorage.removeItem(SELECTED_STORE_KEY);
      }
    },
    LOGOUT(state) {
      state.user = null;
      state.selectedStoreId = null;
      state.currencyCode = "GHS";
      state.currencySymbol = "GH₵ ";
      JwtService.destroyToken();
      JwtService.destroyUser();
      window.localStorage.removeItem(SELECTED_STORE_KEY);
      window.localStorage.removeItem(CURRENCY_STORAGE_KEY);
    },
    SET_CURRENCY(state, { currencyCode, currencySymbol }) {
      state.currencyCode = currencyCode || "GHS";
      state.currencySymbol = currencySymbol || "GH₵ ";
      window.localStorage.setItem(
        CURRENCY_STORAGE_KEY,
        JSON.stringify({
          currencyCode: state.currencyCode,
          currencySymbol: state.currencySymbol,
        })
      );
    },
  },
  actions: {
    async loadSettings({ commit, state }) {
      if (!state.user) return;
      try {
        const settings = await ApiService.get("settings");
        commit("SET_CURRENCY", settings);
      } catch (e) {
        /* keep defaults */
      }
    },
  },
    getters: {
    isAuthenticated: (state) => !!state.user,
    currentUser: (state) => state.user,
    selectedStoreId: (state) => state.selectedStoreId,
    userStores: (state) => state.user?.stores || [],
    selectedStore: (state) => {
      const storeId = state.selectedStoreId;
      return state.user?.stores?.find((s) => s.storeId === storeId) || null;
    },
    permissions: (state) => {
      const storeId = state.selectedStoreId;
      const store = state.user?.stores?.find((s) => s.storeId === storeId);
      return store?.permissions || [];
    },
    hasPermission: (state, getters) => (perm) => {
      const perms = getters.permissions;
      return perms.includes("ALL") || perms.includes(perm);
    },
    currencyCode: (state) => state.currencyCode,
    currencySymbol: (state) => state.currencySymbol,
  },
});
